import { ExportType, Frequency, ScheduleFrequency } from "PFApp/booking/types/export_booking";
import { ActionIcon } from "PFComponents/action_icon";
import SectionBox from "PFComponents/box/section_box";
import { Button } from "PFComponents/button";
import ButtonGroup from "PFComponents/button_group/button_group";
import { LoadingDots } from "PFComponents/loading_dots";
import Modal from "PFComponents/modal/modal";
import { useGetTemplate } from "PFCore/hooks/queries/bookings/manage_reports/use_get_template";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ExportWizardContent } from "../export_wizard_content";
import { decamelizeFilters } from "../export_wizard_content/export_fields_utils";
import { convertFieldsResponse, getTemplateReportType } from "../export_wizard_content/helpers";
import { useExportWizardModalContext } from "../export_wizard_modal_provider";
import css from "./export_wizard_modal.module.scss";

export const ExportWizardModal = (): JSX.Element => {
  const {
    close,
    state: { data },
    updateData
  } = useExportWizardModalContext();
  const {
    currentTab,
    schedule: { frequency }
  } = data || {};
  const { t } = useTranslation("bookingModule", { keyPrefix: "reports" });
  const { data: template, isFetching } = useGetTemplate();

  useEffect(() => {
    if (template) {
      updateData({
        frequency: (template.aggregateBy || Frequency.Daily) as Frequency,
        selectedFilters: decamelizeFilters(template.filters),
        selectedFields: convertFieldsResponse(template.fields),
        reportType: getTemplateReportType(template),
        currentTab: "fields",
        ...(template.name
          ? { selectedTemplate: template, exportType: ExportType.Template }
          : { exportType: ExportType.Custom })
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  const generateButtonLabel = useMemo(() => {
    if (frequency === ScheduleFrequency.No) {
      return t("exportWizard.generate");
    }

    const frequencyLabel = t(`frequency.${frequency}`);

    return `${t("exportWizard.generate")} ${frequencyLabel}`;
  }, [frequency, t]);

  const handleGenerate = () => {
    updateData({
      modals: { reportNameOpen: true }
    });
  };

  const handleChevron = () => {
    updateData({
      modals: { scheduleSettingsOpen: true }
    });
  };

  const handleClose = () => {
    close();
  };

  return (
    //@ts-ignore-line
    <Modal
      closeOnBackdropClick
      handleClose={handleClose}
      classes={{
        dialog: css.dialog
      }}
    >
      <SectionBox className={css.wrapper}>
        {isFetching ? (
          <LoadingDots circlesEnabled pageViewEnabled />
        ) : (
          <>
            <div className={css.header}>{t("exportWizard.exportReport")}</div>
            <ActionIcon name="cross" size="xs" onClick={handleClose} classes={{ root: css.close }} />

            <ExportWizardContent />

            <div className={css.footer}>
              <Button text={t("exportWizard.cancel")} kind="secondary" onClick={handleClose} />
              {currentTab === "export" && (
                <ButtonGroup autoWidth={true} compact={true}>
                  <Button text={generateButtonLabel} onClick={handleGenerate} />
                  <Button icon="chevron-down" onClick={handleChevron} className={css.settings} />
                </ButtonGroup>
              )}
            </div>
          </>
        )}
      </SectionBox>
    </Modal>
  );
};
